import { Action, AnyAction, Reducer } from "redux";
import { produce } from "immer";

import { bridge } from "@multiweb/reactCore/bridge";
import { IDataSourceAttribute } from "@multiweb/reactCore/datasource";
import { TypedObject } from "@multiweb/types/react";
import { EMPTY_OBJECT } from "./constants";

const { generateUUID } = bridge;

/** Vytvoří pole zadané délky a naplní jej indexy */
export const createSequenceArray = (length: number): number[] => {
    const sequenceArray = [];
    for (let i = 0; i < length; i++) {
        sequenceArray.push(i);
    }
    return sequenceArray;
};

/** Vytvoří pole zadané délky a naplní jej zadaným textovým řetězcem doplněným o index. */
export const createSequenceArrayString = (length: number, text: string): string[] => {
    const sequenceArray: string[] = [];
    for (let i = 0; i < length; i++) {
        sequenceArray.push(text + i);
    }
    return sequenceArray;
};

/** Vytvoří pole zadané délky a naplní jej prázdnými textovými řetězcemi. */
export const createArrayWithEmptyStrings = (count: number) => {
    const array: string[] = [];
    for (let i = 0; i < count; i++) {
        array.push("");
    }
    return array;
};

/**
 * Vytvoří náhodné pole čísel v zadaném rosahu a převede je na textové řetězce.
 * @param count Požadovaná velikost pole.
 * @param from Rozsah - generovat od tohoto čísla.
 * @param to Rozsah - generovat do tohoto čísla.
 */
export const createRandomStringArray = (count: number, from: number, to: number): string[] => {
    const randomArray = [];
    for (let i = 0; i < count; i++) {
        randomArray.push((Math.floor(Math.random() * (to - from + 1)) + from).toString());
    }
    return randomArray;
};

/**
 * Vytvoří pole klíčů.
 * @param keyCount Počet klíčů k vytvoření.
 * @param prefix Případná předpona pro klíče.
 * @param sufix Případná přípona pro klíče.
 * @param random Pokud bude false, použijí se jako klíče indexy. Výchozí hodnota je true a v takovém případě použije náhodné čísla klíčů.
 */
export const createKeys = (keyCount: number, prefix = "", sufix = "", random = true) => createSequenceArray(keyCount)
    .map(index => `${prefix}_${random ? generateUUID() : index}_${sufix}`);

/** Vytvoří pole klíčů pro skeletony o zadané délce s náhodnými ID, které mají předponu *row-skeleton*. */
/**
 *
 * @param skeletonCount Počet skeletonů k vytvoření.
 * @param random Pokud bude false, použijí se jako klíče indexy. Výchozí hodnota je true a v takovém případě použije náhodné čísla klíčů.
 */
export const createSkeletonRowsKeys = (skeletonCount: number, random = true): string[] =>
    createKeys(skeletonCount, "row-skeleton", undefined, random);

/**
 * Vytvoří obecný datasource atribut se zadaným názvem, případně i s dalšími předanými vlastnostmi. Do ostatních vlastností doplní null.
 * @param attributeName Název atributu
 * @param otherParams Další vlastnosti atributu
 */
export const createDataSourceAttribute = (attributeName: string, otherParams?: Partial<Omit<IDataSourceAttribute, "attributeName">>): IDataSourceAttribute => ({
    attributeName,
    locKey: otherParams?.locKey ?? null,
    hidden: otherParams?.hidden ?? null,
    isIcon: otherParams?.isIcon ?? null,
    iconName: otherParams?.iconName ?? null,
    backgroundAttrName: otherParams?.backgroundAttrName ?? null,
    displayFormat: otherParams?.displayFormat ?? null,
    attributeRendererKey: otherParams?.attributeRendererKey ?? null,
    trimValue: otherParams?.trimValue === undefined ? true : otherParams?.trimValue,
    extendedProperties: otherParams?.extendedProperties ?? EMPTY_OBJECT,
});

/**
 * Vytvoří datasource atribut se zadaným názvem, případně i s dalšími předanými vlastnostmi. Do ostatních vlastností doplní null.
 * @param attributeName Název atributu
 * @param otherParams Další vlastnosti atributu
 */
export const createDataSourceAttributeFromType = <T>(name: keyof T, otherParams?: Partial<Omit<IDataSourceAttribute, "attributeName">>) => createDataSourceAttribute(name.toString(), otherParams);

/**
 * Funkce pro vytvoření reduceru, který mapuje instance podle klíče. Vložený reducer je použit pro každou instanci zvlášť.
 * Pokud klíč neexistuje, akce je ignorována.
 *
 * @param reducer Reducer, který se používá pro každou instanci.
 * @param instanceKeyResolver Selector funkce, která z akce vrátí klíč.
 */
export const createInstanceMapReducer = <S, A extends Action = AnyAction>(
    reducer: Reducer<S, A>,
    instanceKeyResolver: (action: A) => string | undefined,
) => (state: TypedObject<S>, action: A) => {
        const instanceKey = instanceKeyResolver(action);
        if (instanceKey) {
            const instanceState = reducer(state[instanceKey], action);
            return produce(state, (draft: any) => {
                draft[instanceKey] = instanceState;
            });
        } else {
            return state;
        }
    };
